import { Injectable } from '@angular/core';
import { CoverpathUtils } from '../../../../../coverpath-common/js/static/CoverpathUtils';
import { BaseQuote } from '../../../../../common/js/quote/BaseQuote';
import { Utils } from '../../../../../common/js/Utils';
import { EmailProps, InvitationEmailSubjectAndBody, InviteEmailType, MailToInfo } from '../../types/Types';
import { AgentAmorphicService } from '../amorphic/amorphic.service';
import { MailToService } from './mailto.service';
import { Product } from '../../../../../common/js/static/Product';

@Injectable()
export class ShareLinkService {
  constructor(private amorphicService: AgentAmorphicService) {}

  private cleanupForSandbox(link: string): string {
    return link.replace('http://localhost', `${window.location.protocol}//${window.location.hostname}`);
  }

  /**
   * if there is a generic link already made, return it.
   * otherwise, make a new one.
   * @returns {string}
   */
  getAgentGenericLink(): string {
    const agentShareController = this.amorphicService.controller.agentShareController;
    const link = this.cleanupForSandbox(this.amorphicService.controller.getAgent().genericLink);
    agentShareController.inviteLink = link;
    return link;
  }

  private async getPersonalizedLinkToCopy(): Promise<string> {
    const agentShareController = this.amorphicService.controller.agentShareController;
    const link = this.cleanupForSandbox(await agentShareController.submitCustomerForLink());
    agentShareController.inviteLink = link;
    return link;
  }

  public async generateAdvisorAssistantApplicationLink(isAdvisorEdit: boolean): Promise<string> {
    const amorphicController = this.amorphicService.controller;
    const agentShareController = amorphicController.agentShareController;
    const link = this.cleanupForSandbox(await agentShareController.generateShareLink(amorphicController.customer));
    agentShareController.inviteLink = link;
    const emailContent = this.getMailToInfo(
      isAdvisorEdit ? InviteEmailType.ADVISOR_EDIT : InviteEmailType.ADVISOR_ASSISTED,
      link
    );

    MailToService.openMailToEmail(emailContent);
    return link;
  }
  /**
   * Generates an email's subject and body depending on the type of invitation the agent is trying to implement.
   * If the link is provided, this function doesn't make an extra call to generate a link.
   *
   * @param {InviteEmailType} invitationType :: Type of invitation the agent is proceeding with.
   * @param {string} generatedLink :: Link that has been generated.
   * @returns {Promise<InvitationEmailSubjectAndBody>}
   */
  public async generatePersonalizedEmailSubjectBody(
    invitationType: InviteEmailType,
    generatedLink?: string
  ): Promise<InvitationEmailSubjectAndBody> {
    const subjectAndBody: InvitationEmailSubjectAndBody = {
      subject: '',
      body: '',
    };
    const amorphicController = this.amorphicService.controller;
    const agentShareController = amorphicController.agentShareController;
    let link = '';
    if (!!generatedLink) {
      link = generatedLink;
    } else if (invitationType === InviteEmailType.QUOTE) {
      link = await this.getPersonalizedLinkToCopy();
    } else if (invitationType === InviteEmailType.ADVISOR_ASSISTED || invitationType === InviteEmailType.ADVISOR_EDIT) {
      link = this.cleanupForSandbox(await agentShareController.generateShareLink(amorphicController.customer));
    } else if (invitationType === InviteEmailType.GENERIC) {
      link = this.getAgentGenericLink();
    } else {
      link = 'link has error...';
    }
    const emailContent = this.getMailToInfo(invitationType, link);
    subjectAndBody.subject = MailToService.getEmailSubject(emailContent);
    subjectAndBody.body = MailToService.getEmailBody(emailContent);

    return subjectAndBody;
  }

  public prepareEmailForQuote(invitationType: InviteEmailType, quote: BaseQuote): EmailProps {
    let termQuote: BaseQuote;
    let wholeQuote: BaseQuote;
    let termPremium: number;
    let wholePremium: number;

    if (quote.productType === Product.productTypes.Whole.code) {
      wholeQuote = quote;
      termQuote = quote.secondaryQuote;
    }
    if (quote.productType === Product.productTypes.Term.code) {
      termQuote = quote;
    }

    if (wholeQuote) {
      wholePremium = wholeQuote.premium;
      if (wholeQuote.waiverOfPremium) {
        wholePremium += wholeQuote.waiverOfPremiumAmount;
      }
    }

    if (termQuote) {
      termPremium = termQuote.premium;
      if (termQuote.waiverOfPremium) {
        termPremium += termQuote.waiverOfPremiumAmount;
      }
    }

    const amorphicController = this.amorphicService.controller;

    const isQuoteOrAdvisorAssisted: boolean =
      invitationType === InviteEmailType.QUOTE || invitationType === InviteEmailType.ADVISOR_ASSISTED;
    const isAdvisorAssisted: boolean = invitationType === InviteEmailType.ADVISOR_ASSISTED;

    const primaryPolicy = amorphicController.customer.application.primaryPolicy;
    const emailContent: MailToInfo = {
      invitationType: invitationType,
      clientEmail: quote.customer.email,
      agentEmail: amorphicController.getAgent().person.email,
      agentFirstName: amorphicController.getAgent().displayName,
      clientFirstName: quote.customer.firstName,
      inviteLink: this.cleanupForSandbox(`${amorphicController.clientInviteUrl}${quote.invite}`),
      isJointApp: !!quote.secondaryQuote,
      term: isQuoteOrAdvisorAssisted && termQuote ? termQuote.term : undefined,
      termFaceAmount: isQuoteOrAdvisorAssisted && termQuote ? termQuote.faceAmount : undefined,
      wholeFaceAmount: isQuoteOrAdvisorAssisted && wholeQuote ? wholeQuote.faceAmount : undefined,
      termPremium: isQuoteOrAdvisorAssisted && termPremium ? Utils.round(termPremium, 2) : undefined,
      wholePremium: isQuoteOrAdvisorAssisted && wholePremium ? Utils.round(wholePremium, 2) : undefined,
      termPaymentFrequency: isAdvisorAssisted && termQuote ? termQuote.paymentFrequency : undefined,
      wholePaymentFrequency: isAdvisorAssisted && wholeQuote ? wholeQuote.paymentFrequency : undefined,
      productType: isAdvisorAssisted ? primaryPolicy.productType : undefined,
      insuredFullName: Utils.toCapitalizedWords(primaryPolicy.insured.person.fullName),
      ownerEqualsInsured: primaryPolicy.isOwnerSameAsInsured(),
    };
    const emailInfo: EmailProps = {
      to: quote.customer.email,
      subject: MailToService.getEmailSubject(emailContent),
      body: MailToService.getEmailBody(emailContent),
    };
    return emailInfo;
  }

  public prepareEmailForConversion(invitationType: InviteEmailType, invite: string): EmailProps {
    const amorphicController = this.amorphicService.controller;

    const emailContent: MailToInfo = {
      invitationType: invitationType,
      clientEmail: amorphicController.customer.email,
      agentEmail: amorphicController.getAgent().person.email,
      agentFirstName: amorphicController.getAgent().displayName,
      clientFirstName: amorphicController.customer.firstName,
      inviteLink: this.cleanupForSandbox(`${amorphicController.clientConversionInviteUrl}${invite}`),
    };
    const emailInfo: EmailProps = {
      to: amorphicController.customer.email,
      subject: MailToService.getEmailSubject(emailContent),
      body: MailToService.getEmailBody(emailContent),
    };
    return emailInfo;
  }

  public getResendEmailForConversion(invitationType: InviteEmailType, quote: BaseQuote): EmailProps {
    const amorphicController = this.amorphicService.controller;

    const emailContent: MailToInfo = {
      invitationType: invitationType,
      clientEmail: amorphicController.customer.email,
      agentEmail: amorphicController.getAgent().person.email,
      agentFirstName: amorphicController.getAgent().displayName,
      clientFirstName: amorphicController.customer.firstName,
      inviteLink: this.cleanupForSandbox(`${amorphicController.clientConversionInviteUrl}${quote.invite}`),
    };
    const emailInfo: EmailProps = {
      to: amorphicController.customer.email,
      subject: MailToService.getEmailSubject(emailContent),
      body: MailToService.getEmailBody(emailContent),
    };
    return emailInfo;
  }

  getResendEmailInfo(invitationType: InviteEmailType, quote: BaseQuote): EmailProps {
    const amorphicController = this.amorphicService.controller;
    const link = this.cleanupForSandbox(`${amorphicController.clientInviteUrl}${quote.invite}`);
    const emailContent = this.getMailToInfo(invitationType, link);
    const emailInfo: EmailProps = {
      to: CoverpathUtils.getEmailForSendingAApp(amorphicController.customer),
      subject: MailToService.getEmailSubject(emailContent),
      body: MailToService.getEmailBody(emailContent),
    };

    return emailInfo;
  }

  getMailToInfo(invitationType: InviteEmailType, link: string): MailToInfo {
    let termPremium;
    let wholePremium;
    let _showPremiumToClient;
    const amorphicController = this.amorphicService.controller;

    //all the things we needed for Quote will be included for AdvisorAssisted
    const isQuoteOrAdvisorAssisted: boolean =
      invitationType === InviteEmailType.QUOTE ||
      invitationType === InviteEmailType.ADVISOR_ASSISTED ||
      invitationType === InviteEmailType.ADVISOR_EDIT;
    const isAdvisorAssisted: boolean =
      invitationType === InviteEmailType.ADVISOR_ASSISTED || invitationType === InviteEmailType.ADVISOR_EDIT;

    if (invitationType === InviteEmailType.GENERIC) {
      return {
        inviteLink: link,
        invitationType,
        agentFirstName: amorphicController.getAgent().displayName,
        agentEmail: amorphicController.getAgent().person.email,
      } as MailToInfo;
    }

    if (isQuoteOrAdvisorAssisted) {
      const quote = amorphicController.customer?.wholeLifeQuote || amorphicController.customer?.termQuote;
      _showPremiumToClient = quote.showPremiumToClient;
    }

    const isTermPolicy = amorphicController.customer.application.primaryPolicy.isTermLifePolicy();
    const isWholeLifePolicy = amorphicController.customer.application.primaryPolicy.isWholeLifePolicy();

    let termFaceAmount: number;
    let wholeFaceAmount: number;
    let termPaymentFrequency: string;
    let wholePaymentFrequency: string;
    let term: number;
    let wholeTerm: number;

    if (amorphicController.customer.application.isJointApp()) {
      termFaceAmount = amorphicController.customer.application.secondaryPolicy.faceAmountWithPendingAmendment();
      wholeFaceAmount = amorphicController.customer.application.primaryPolicy.faceAmountWithPendingAmendment();
      termPaymentFrequency = amorphicController.customer.application.secondaryPolicy.paymentFrequency;
      wholePaymentFrequency = amorphicController.customer.application.primaryPolicy.paymentFrequency;
      term = amorphicController.customer.application.secondaryPolicy.term;
      wholeTerm = amorphicController.customer.application.primaryPolicy.term;
      termPremium =
        amorphicController.customer.application.secondaryPolicy.premiumBreakdown.getTotalWithPendingAmendment();
      wholePremium =
        amorphicController.customer.application.primaryPolicy.premiumBreakdown.getTotalWithPendingAmendment();
    } else {
      termFaceAmount = isTermPolicy
        ? amorphicController.customer.application.getTotalFaceAmountWithPendingAmendment()
        : undefined;
      termPaymentFrequency = isTermPolicy
        ? amorphicController.customer.application.primaryPolicy.paymentFrequency
        : undefined;
      term = isTermPolicy ? amorphicController.customer.application.primaryPolicy.term : undefined;
      wholeTerm = isWholeLifePolicy ? amorphicController.customer.application.primaryPolicy.term : undefined;
      wholeFaceAmount = isWholeLifePolicy
        ? amorphicController.customer.application.getTotalFaceAmountWithPendingAmendment()
        : undefined;
      wholePaymentFrequency = isWholeLifePolicy
        ? amorphicController.customer.application.primaryPolicy.paymentFrequency
        : undefined;
      termPremium = isTermPolicy
        ? amorphicController.customer.application.primaryPolicy.premiumBreakdown.getTotalWithPendingAmendment()
        : undefined;
      wholePremium = isWholeLifePolicy
        ? amorphicController.customer.application.primaryPolicy.premiumBreakdown.getTotalWithPendingAmendment()
        : undefined;
    }

    const primaryPolicy = amorphicController.customer.application.primaryPolicy;
    const emailContent: MailToInfo = {
      invitationType: invitationType,
      clientEmail: CoverpathUtils.getEmailForSendingAApp(amorphicController.customer),
      agentEmail: amorphicController.getAgent().person.email,
      agentFirstName: amorphicController.getAgent().displayName,
      // OwnerPerson first name is empty when owner equals insured, use primaryCustomer first name instead
      clientFirstName: primaryPolicy.owner.emailName || amorphicController.customer.primaryCustomer.person.firstName,
      inviteLink: link,
      isJointApp: isQuoteOrAdvisorAssisted && amorphicController.customer.application.isJointApp(),
      term,
      wholeTerm,
      termFaceAmount,
      wholeFaceAmount,
      termPremium,
      wholePremium,
      // Currently, joint app can only has one payment frequency, but we still have two frequencies here in case
      // we will support different payment frequency for whole and term in the future
      termPaymentFrequency: Utils.toCapitalizedWords(termPaymentFrequency),
      wholePaymentFrequency: Utils.toCapitalizedWords(wholePaymentFrequency),
      productType: isAdvisorAssisted ? primaryPolicy.productType : undefined,
      insuredFullName: Utils.toCapitalizedWords(primaryPolicy.insured.person.fullName),
      ownerEqualsInsured: primaryPolicy.isOwnerSameAsInsured(),
      showPremiumToClient: _showPremiumToClient,
    };

    return emailContent;
  }
}
